import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { compose, isSupportedByDefault, getCookie, setCookie, getExpiration, trackEventOnComponentPrint, trackPage, trackEvent } from '@vpp-frontend-components/common';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import get from 'lodash/get';

// migraded 2-10-2022
function useEnhancedReducer(reducer, initialState) {
  let middlewares = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
  const [hookState, setHookState] = useState(initialState);
  const state = useRef(hookState);
  const dispatch = action => {
    const newState = reducer(state.current, action);
    state.current = newState;
    setHookState(newState);
    return action;
  };
  let enhancedDispatch;
  const store = {
    getState: () => state.current,
    dispatch: function () {
      return enhancedDispatch(...arguments);
    }
  };
  const chain = middlewares.map(middleware => middleware(store));
  enhancedDispatch = compose(...chain)(dispatch);
  return [hookState, enhancedDispatch];
}
const DEFAULT_IO_OPTIONS = {
  root: null,
  rootMargin: '0px',
  threshold: 1.0
};

/**
 * @TODO
 * make a box for actions so it sostaints its height
 */
const useIntersectionObserver = function (ref) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let minIntersection = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  const [isIntersecting, setIntersecting] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ioOptions = _objectSpread(_objectSpread({}, DEFAULT_IO_OPTIONS), options);
  useEffect(() => {
    const observer = new window.IntersectionObserver(_ref => {
      let [entry] = _ref;
      setIntersecting(entry.isIntersecting && entry.intersectionRatio > minIntersection);
    }, ioOptions);
    const {
      current
    } = ref;
    if (!current) {
      // este chequeo es para evitar romper todo el contexto de React si nos pasaron un ref inválido.

      // eslint-disable-next-line no-console
      console.error('Intersection Observer not connected because current ref is null');
      return () => {};
    }
    observer.observe(current);
    return () => observer.unobserve(current);
  }, [ref, ioOptions, minIntersection]);
  return [isIntersecting];
};
const DEFAULT_OPTIONS$2 = {
  threshold: 0.45
};

/**
 * Custom hook to fetch a deferred component using Intersection Observer.
 * @param {Function} deferred - Function to execute the fetch
 * @param {string} id - Id of the product to fetch
 * @param {Object} options - Custom configuration for each component
 * @param {string} options.threshold - Percentage of the component that should be in viewport to consider it as "seen"
 */
const useFetchDeferredComponent = (onFetchDeferredComponent, id, options) => {
  const [isFetched, setIsFetched] = useState(false);
  const refView = useRef();
  const [isIntersecting] = useIntersectionObserver(refView, _objectSpread(_objectSpread({}, DEFAULT_OPTIONS$2), options));
  useEffect(() => {
    if (isIntersecting && !isFetched) {
      onFetchDeferredComponent(id);
      setIsFetched(true);
    }
  }, [isIntersecting, isFetched, onFetchDeferredComponent, id]);
  return refView;
};
function useInterval(callback, delay) {
  const savedCallback = useRef();
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect(() => {
    function tick(clean) {
      savedCallback.current(clean);
    }
    let clean;
    const id = setInterval(() => tick(clean), delay);
    clean = () => clearInterval(id);
    return () => clean();
  }, [delay]);
}
const DEFAULT_OPTIONS$1 = {
  rootMargin: '400px 0px',
  threshold: 0
};
const useLazyStyles = (stylesObj, options) => {
  const refView = useRef();
  const [isIntersecting] = useIntersectionObserver(refView, _objectSpread(_objectSpread(_objectSpread({}, DEFAULT_OPTIONS$1), options), {}, {
    root: refView.current
  }));
  const [isLoaded, setIsLoaded] = useState(false);
  useEffect(() => {
    if (isIntersecting && !isLoaded) {
      setIsLoaded(true);
      const styleTag = document.createElement('style');
      styleTag.type = 'text/css';
      const cssProperties = Object.values(stylesObj).join(' ');
      styleTag.appendChild(document.createTextNode(cssProperties));
      document.head.appendChild(styleTag);
    }
  }, [isIntersecting, isLoaded, stylesObj]);
  return refView;
};
function useLocalStorage(key, initialValue) {
  const isLocalStorageSupported = useMemo(() => isSupportedByDefault(), []);
  const [storedValue, setStoredValue] = useState(() => {
    if (!isLocalStorageSupported) {
      return;
    }
    const valueAsString = localStorage.getItem(key);
    if (valueAsString) {
      // eslint-disable-next-line consistent-return
      return JSON.parse(valueAsString);
    }
    if (initialValue) {
      localStorage.setItem(key, JSON.stringify(initialValue));

      // eslint-disable-next-line consistent-return
      return initialValue;
    }
  });
  const setValue = value => {
    if (!isLocalStorageSupported) {
      return;
    }
    const valueToStore = value instanceof Function ? value(storedValue) : value;
    localStorage.setItem(key, JSON.stringify(valueToStore));
    setStoredValue(valueToStore);
  };
  return [storedValue, setValue, isLocalStorageSupported];
}
const useModelSelector = (unitTitle, modelId, models, modelUnits, modelLabel, modelTitle, getModelUnits, onClick) => {
  const [open, setOpen] = useState(false);
  const [listTitle, setListTitle] = useState(unitTitle);
  const [selectedModel, setSelectedModel] = useState(modelId ? models.find(model => model.id === parseInt(modelId, 10)) : models[0]);
  const [listItem, setListItem] = useState(modelUnits ? modelUnits[selectedModel.id] : []);
  const [selectedUnit, setSelectedUnit] = useState({});
  const onClose = () => {
    setOpen(false);
  };
  const onClickHandler = e => {
    e.preventDefault();
    if (onClick) {
      onClick();
    }
    if (e.currentTarget.id === modelLabel.text) {
      setListTitle(modelTitle);
      setListItem(models);
    } else {
      setListTitle(unitTitle);
      setListItem(modelUnits[selectedModel.id]);
    }
    setOpen(true);
  };
  const updateUnit = (e, itemId) => {
    e.preventDefault();
    const clickedUnit = modelUnits[selectedModel.id].find(unit => unit.id === parseInt(itemId, 10));
    setSelectedUnit(clickedUnit);
    onClose();
  };
  const updateModel = function (e, itemId) {
    let step = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
    e.preventDefault();
    const selectedModelId = parseInt(itemId, 10);
    const clickedModel = models.find(model => model.id === selectedModelId);
    getModelUnits(selectedModelId, 'mobile', {
      selectedModelId,
      selectedUnit,
      step
    });
    setSelectedModel(clickedModel);
    onClose();
  };
  const usePrevious = value => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };
  const prevSelectedModel = usePrevious(selectedModel);
  const prevSelectedModelId = prevSelectedModel ? prevSelectedModel.id : null;
  const prevModelUnits = usePrevious(modelUnits);
  useEffect(() => {
    const prevModelUnitsKeysLength = prevModelUnits ? Object.keys(prevModelUnits).length : 0;
    const modelUnitsKeyLength = modelUnits ? Object.keys(modelUnits).length : 0;
    if ((prevSelectedModelId !== selectedModel.id || prevModelUnitsKeysLength !== modelUnitsKeyLength) && modelUnits[selectedModel.id]) {
      setSelectedUnit(modelUnits[selectedModel.id][0]);
    }
  }, [modelUnits, selectedUnit, selectedModel, prevSelectedModelId, prevModelUnits]);
  return [{
    open,
    listTitle,
    listItem,
    selectedUnit,
    selectedModel,
    onClose,
    onClickHandler
  }, {
    setOpen,
    updateUnit,
    updateModel
  }];
};
const useIsMounted = () => {
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  return isMounted;
};

/* https://usehooks.com/#useOnClickOutside */

/** global document */
const useOnClickOutside = (ref, handler) => {
  const listener = useCallback(event => {
    // Do nothing if clicking ref's element or descendent elements
    if (!ref.current || ref.current.contains(event.target)) {
      return;
    }
    handler(event);
  }, [ref, handler]);
  useEffect(() => {
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  },
  // Add ref and handler to effect dependencies
  // It's worth noting that because passed in handler is a new ...
  // ... function on every render that will cause this effect ...
  // ... callback/cleanup to run every render. It's not a big deal ...
  // ... but to optimize you can wrap handler in useCallback before ...
  // ... passing it into this hook.
  [ref, handler, listener]);
};

/**  global document */
/**
 * Creates DOM element to be used as React root.
 * @returns {HTMLElement}
 */
function createRootElement(id) {
  const rootContainer = document.createElement('div');
  rootContainer.setAttribute('id', id);
  return rootContainer;
}

/**
 * Appends element as last child of body.
 * @param {HTMLElement} rootElem
 */
function addRootElement(rootElem) {
  document.body.insertBefore(rootElem, document.body.lastElementChild.nextElementSibling);
}

/**
 * Hook to create a React Portal.
 * Automatically handles creating and tearing-down the root elements (no SRR
 * makes this trivial), so there is no need to ensure the parent target already
 * exists.
 * @example
 * const target = usePortal(id, [id]);
 * return createPortal(children, target);
 * @param {String} id The id of the target container, e.g 'modal' or 'spotlight'
 * @returns {HTMLElement} The DOM node to use as the Portal target.
 */
function usePortal(id) {
  const rootElementRef = useRef(null);
  useEffect(() => {
    // Look for existing target dom element to append to
    const existingParent = document.querySelector(`#${id}`);
    // Parent is either a new root or the existing dom element
    const parentElement = existingParent || createRootElement(id);

    // If there is no existing DOM element, add a new one.
    if (!existingParent) {
      addRootElement(parentElement);
    }
    const {
      current
    } = rootElementRef;

    // Add the detached element to the parent
    parentElement.appendChild(current);
    return () => {
      current.remove();
      if (parentElement.childNodes.length === -1) {
        parentElement.remove();
      }
    };
  }, [id]);

  /**
   * It's important we evaluate this lazily:
   * - We need first render to contain the DOM element, so it shouldn't happen
   *   in useEffect. We would normally put this in the constructor().
   * - We can't do 'const rootElementRef = useRef(document.createElement('div))',
   *   since this will run every single render (that's a lot).
   * - We want the ref to consistently point to the same DOM element and only
   *   ever run once.
   * @link https://reactjs.org/docs/hooks-faq.html#how-to-create-expensive-objects-lazily
   */
  function getRootElem() {
    if (!rootElementRef.current) {
      rootElementRef.current = document.createElement('div');
    }
    return rootElementRef.current;
  }
  return getRootElem();
}
function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}
const useScrollElement = _ref => {
  let {
    container,
    element,
    shouldScroll
  } = _ref;
  const pixelsToScrollRef = useRef();
  const getPixelsToScroll = targetElement => {
    var _window;
    const viewportWidth = ((_window = window) === null || _window === void 0 ? void 0 : _window.innerWidth) || 0;
    const targetElementBoundingRect = targetElement.getBoundingClientRect();
    return targetElementBoundingRect.left - viewportWidth / 2 + targetElementBoundingRect.width / 2;
  };
  useEffect(() => {
    pixelsToScrollRef.current = getPixelsToScroll(element.current);
  }, [element]);
  useEffect(() => {
    let scrollTimeout;
    if (shouldScroll) {
      scrollTimeout = setTimeout(() => {
        var _container$current;
        if (container !== null && container !== void 0 && (_container$current = container.current) !== null && _container$current !== void 0 && _container$current.scrollTo) {
          container.current.scrollTo({
            left: pixelsToScrollRef.current,
            behavior: 'smooth'
          });
        } else {
          var _element$current;
          (_element$current = element.current) === null || _element$current === void 0 || _element$current.scrollIntoView({
            behavior: 'auto',
            inline: 'center'
          });
        }
      }, 100);
    }
    return () => scrollTimeout && clearTimeout(scrollTimeout);
  }, [element, container, shouldScroll]);
  const scrollToElement = useCallback(() => {
    var _container$current2;
    if (container !== null && container !== void 0 && (_container$current2 = container.current) !== null && _container$current2 !== void 0 && _container$current2.scrollTo) {
      container.current.scrollTo({
        left: pixelsToScrollRef.current,
        behavior: 'smooth'
      });
    }
  }, [container]);
  return scrollToElement;
};
const DEFAULT_ATTRS = {
  path: '/'
};

// Set this fn for export
const updateCloseFn = _ref => {
  let {
    cookieName,
    userConsent,
    id,
    attrs = DEFAULT_ATTRS,
    addView
  } = _ref;
  const cookie = getCookie(cookieName);
  const now = Math.floor(Date.now() / 1000);
  const {
    close_cnt: closeCount,
    view_cnt: viewCount
  } = get(cookie, `${cookieName}.${id}`);
  const newValue = {
    close_cnt: closeCount + 1,
    close_time: now
  };
  if (addView) {
    newValue.view_cnt = viewCount + 1;
    newValue.view_time = now;
  }
  if (userConsent) {
    setCookie(cookieName, {
      [id]: newValue
    }, _objectSpread(_objectSpread({}, attrs), {}, {
      expires: getExpiration(365)
    }));
  }
};
const useViewCounter = _ref2 => {
  let {
    id,
    visible,
    storable,
    cookieName,
    userConsent,
    attrs = DEFAULT_ATTRS
  } = _ref2;
  useEffect(() => {
    if (storable) {
      if (!attrs.expires) {
        attrs.expires = getExpiration(365);
      }
      const now = Math.floor(Date.now() / 1000);
      let status = {
        view_cnt: visible ? 1 : 0,
        close_cnt: 0,
        view_time: visible ? now : 0,
        close_time: 0
      };
      const cookie = getCookie(cookieName);
      if (cookie) {
        let cookieStatus = get(cookie, `${cookieName}.${id}`, {
          view_cnt: 0,
          close_cnt: 0
        });

        // Aunque la linea anterior define un default, se reportó un error el cual generaba un
        // valor de cookieStatus undefined
        cookieStatus = cookieStatus || {};
        status = _objectSpread(_objectSpread({}, status), cookieStatus);
        if (visible) {
          status.view_cnt = cookieStatus.view_cnt + 1;
          status.view_time = now;
        }
      }
      setCookie(cookieName, {
        [id]: status
      }, _objectSpread({}, attrs));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const updateCloseStatus = isPreloadModal => {
    updateCloseFn({
      cookieName,
      userConsent,
      id,
      attrs,
      addView: isPreloadModal
    });
  };
  return storable ? updateCloseStatus : null;
};
const DEFAULT_OPTIONS = {
  threshold: 0.5
};

/**
 * Custom hook to track a component using Intersection Observer.
 * @param {Object} track - Element to be tracked using custom lib for vpp-frontend
 * @param {Object} options - Custom configuration for each component
 * @param {string} options.threshold - Percentage of the component that should be in viewport to consider it as "seen"
 * @param {Boolean} as_component_print - Determine if the track go to component_prints insted track
 */
const useTrackView = function (track, options) {
  let type = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
  const trackMethodByType = type === 'view' ? trackPage : trackEvent;
  const trackMethod = options !== null && options !== void 0 && options.as_component_print ? trackEventOnComponentPrint : trackMethodByType;
  const [isTracked, setIsTracked] = useState(false);
  const refView = useRef();
  const [isIntersecting] = useIntersectionObserver(refView, _objectSpread(_objectSpread({}, DEFAULT_OPTIONS), options));
  useEffect(() => {
    if (isIntersecting && !isTracked) {
      trackMethod(track);
      setIsTracked(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIntersecting, isTracked, track]);
  return refView;
};
export { updateCloseFn, useEnhancedReducer, useFetchDeferredComponent, useIntersectionObserver, useInterval, useIsMounted, useLazyStyles, useLocalStorage, useModelSelector, useOnClickOutside, usePortal, usePrevious, useScrollElement, useTrackView, useViewCounter };
